/**
 * StrategySettingsCard — extracted from SettingsTab.jsx (iter-83).
 *
 * Renders the "Strategy" section: 6 strategy toggles, timeframe selector,
 * SMC min-confidence input, and the 11-pair symbol selector.
 */
import { motion } from 'framer-motion';
import { Crosshair, CheckCircle } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { STRATEGY_CATALOG, TRADABLE_PAIRS } from '../constants/settingsConstants';

export default function StrategySettingsCard({ form, setForm, toggleStrategy, isHybrid, language }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-[#d4af37]/20 p-6"
    >
      <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
        <Crosshair className="h-5 w-5 text-[#d4af37]" />
        {language === 'ar' ? 'الاستراتيجية' : 'Strategy'}
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <label className="block text-sm text-[#71717A] mb-3">
            {language === 'ar'
              ? `الاستراتيجيات المفعّلة (${form.active_strategies.length}/6)`
              : `Active Strategies (${form.active_strategies.length}/6)`}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STRATEGY_CATALOG.map((strat) => {
              const selected = form.active_strategies.includes(strat.id);
              return (
                <button
                  key={strat.id}
                  type="button"
                  role="checkbox"
                  aria-checked={selected}
                  onClick={() => toggleStrategy(strat.id)}
                  className={`flex items-start gap-3 p-3 rounded-xl border text-start transition-all duration-300 ${
                    selected
                      ? 'bg-[#d4af37]/10 border-[#d4af37]/50'
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`strategy-toggle-${strat.id}`}
                >
                  <div className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    selected ? 'bg-[#d4af37] border-[#d4af37]' : 'border-white/30'
                  }`}>
                    {selected && <CheckCircle className="h-3.5 w-3.5 text-black" />}
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${selected ? 'text-[#d4af37]' : 'text-white'}`}>
                      {language === 'ar' ? strat.nameAr : strat.nameEn}
                    </div>
                    <div className="text-xs text-[#71717A] mt-0.5">
                      {language === 'ar' ? strat.descAr : strat.descEn}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {isHybrid && (
            <p className="text-xs text-emerald-400 mt-3">
              {language === 'ar'
                ? `✓ وضع مدمج (${form.active_strategies.length} استراتيجيات) — يجب أن تتفق الأغلبية على نفس الاتجاه لتنفيذ الصفقة`
                : `✓ Combined mode (${form.active_strategies.length} strategies) — majority must agree on direction to execute`}
            </p>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#71717A] mb-2">{language === 'ar' ? 'الفريم الزمني' : 'Timeframe'}</label>
            <Select value={form.timeframe} onValueChange={(v) => setForm({ ...form, timeframe: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="timeframe-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-white/10 text-white">
                <SelectItem value="5m">M5</SelectItem>
                <SelectItem value="15m">M15</SelectItem>
                <SelectItem value="30m">M30</SelectItem>
                <SelectItem value="1h">H1</SelectItem>
                <SelectItem value="4h">H4</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-2">
              {language === 'ar' ? 'حد ثقة SMC الأدنى' : 'SMC Min Confidence'} ({Math.round(form.smc_min_confidence * 100)}%)
            </label>
            <Input
              type="number"
              step="0.05"
              min="0.3"
              max="0.95"
              value={form.smc_min_confidence}
              onChange={(e) => setForm({ ...form, smc_min_confidence: parseFloat(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
              data-testid="smc-confidence-input"
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-semibold text-white">
            {language === 'ar' ? 'الأزواج المتداولة' : 'Trading Symbols'}
            <span className="ms-2 text-xs text-[#71717A]">
              ({form.symbols.length}/{TRADABLE_PAIRS.length})
            </span>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, symbols: TRADABLE_PAIRS.map(p => p.code) })}
              className="text-[10px] px-2 py-1 rounded bg-white/5 border border-white/10 text-[#71717A] hover:text-white"
              data-testid="symbols-select-all"
            >
              {language === 'ar' ? 'الكل' : 'All'}
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, symbols: [] })}
              className="text-[10px] px-2 py-1 rounded bg-white/5 border border-white/10 text-[#71717A] hover:text-white"
              data-testid="symbols-clear"
            >
              {language === 'ar' ? 'إفراغ' : 'Clear'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/10">
          {TRADABLE_PAIRS.map((p) => {
            const active = form.symbols.includes(p.code);
            return (
              <button
                key={p.code}
                type="button"
                onClick={() => setForm({
                  ...form,
                  symbols: active ? form.symbols.filter((s) => s !== p.code) : [...form.symbols, p.code],
                })}
                className={`relative text-xs px-2 py-2.5 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                  active
                    ? 'bg-[#d4af37]/20 border-[#d4af37] text-[#d4af37] shadow-[0_0_12px_rgba(212,175,55,0.25)]'
                    : 'bg-white/5 border-white/10 text-[#71717A] hover:text-white hover:border-white/20'
                }`}
                data-testid={`symbol-toggle-${p.code}`}
              >
                <span className="text-[10px] opacity-70">{p.group}</span>
                <span className="font-mono font-bold">{p.display}</span>
                {active && (
                  <span className="absolute top-1 end-1 w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                )}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-[#71717A] mt-2">
          {language === 'ar'
            ? 'يستخدم البوت والـ AI هذه الأزواج فقط في التحليل والصفقات.'
            : 'The bot and AI only trade and analyse the pairs you select here.'}
        </p>
      </div>
    </motion.div>
  );
}
